import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { SkeletonPostItem } from '../components/SkeletonLoader';
import Pagination from '../components/Pagination';

function Highlight({ text, q }) {
  if (!q || !text) return text;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i} className="pub-highlight">{part}</mark> : part
  );
}

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '0');
  const categorySlug = searchParams.get('categorySlug') || '';
  const q = searchParams.get('q') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // 카테고리 목록 (설명 표시용) — 최초 1회만 로드
  useEffect(() => {
    api.get('/api/public/categories').then((res) => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page };
    if (categorySlug) params.categorySlug = categorySlug;
    if (q) params.q = q;

    api.get('/api/public/posts', { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, categorySlug, q]);

  const activeCategory = categorySlug
    ? categories.find((c) => c.slug === categorySlug) || null
    : null;

  const goPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', p);
    setSearchParams(next);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)
      .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      .toUpperCase();
  };

  const isHero = !q && !categorySlug && page === 0;
  const posts = data?.content || [];

  return (
    <div className="pub-home">
      {isHero && (
        <div className="pub-hero">
          <div className="pub-hero-text">
            <p className="pub-hero-sub">
              <span className="pub-hero-line" />
              HOBBYGOM · 곰이 즐기는 취미생활
            </p>
            <h1 className="pub-hero-title">
              취미에 진심인<br />한 마리의 곰
            </h1>
            <p className="pub-hero-desc">
              나이 마흔, 우리는 여전히 놀고 싶다. 낡은 것이 아닌 익어가는 취미들을 기록합니다.
            </p>
          </div>
          <img
            className="pub-hero-og"
            src="/images/og-image.svg"
            alt="HOBBYGOM - 곰이 즐기는 취미생활"
          />
        </div>
      )}

      {(q || categorySlug) && (
        <div className="pub-filter-info">
          {q && (
            <div className="pub-filter-cat">
              <span className="pub-filter-cat-name">검색: {q}</span>
            </div>
          )}
          {categorySlug && (
            <div className="pub-filter-cat">
              <span className="pub-filter-cat-name">
                {activeCategory?.name || categorySlug}
              </span>
              {activeCategory?.description && (
                <span className="pub-filter-cat-desc">{activeCategory.description}</span>
              )}
            </div>
          )}
          <Link to="/" className="pub-filter-clear">× 필터 지우기</Link>
        </div>
      )}

      {loading ? (
        <section className="pub-posts">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonPostItem key={i} />)}
        </section>
      ) : posts.length === 0 ? (
        <p className="pub-empty">게시글이 없습니다.</p>
      ) : (
        <section className="pub-posts">
          {posts.map((post, idx) => (
            <article key={post.id} className="pub-post-item">
              <div className="pub-post-main">
                <div className="pub-post-left">
                  <span className="pub-post-num">
                    {String(page * 10 + idx + 1).padStart(2, '0')}
                  </span>
                  <div className="pub-post-info">
                    <div className="pub-post-tags">
                      {post.category && (
                        <span className="pub-post-cat">
                          {post.category.name.toUpperCase()}
                        </span>
                      )}
                      {post.aiGenerated && (
                        <span className="pub-ai-tag-sm" title="AI가 작성했거나 AI 도움을 받아 작성된 글입니다">
                          ✦ AI
                        </span>
                      )}
                    </div>
                    <h2 className="pub-post-title">
                      <Link to={`/posts/${post.slug}`}>
                        <Highlight text={post.title} q={q} />
                      </Link>
                    </h2>
                  </div>
                </div>
                <time className="pub-post-date">{formatDate(post.publishedAt)}</time>
              </div>

              {post.summary && (
                <div className="pub-post-expand">
                  <p className="pub-post-summary"><Highlight text={post.summary} q={q} /></p>
                  <Link to={`/posts/${post.slug}`} className="pub-post-read">
                    자세히 보기
                  </Link>
                </div>
              )}
            </article>
          ))}
        </section>
      )}

      <Pagination
        page={page}
        totalPages={data?.totalPages || 0}
        onPageChange={goPage}
        className="pub-pagination"
      />
    </div>
  );
}
